import type { List } from 'immutable';
import { DateTime } from 'luxon';
import { createSelector } from 'reselect';

import { getLogger } from '../../../utils/core';
import { accountsSelectors } from '../../accounts';
import type { Account } from '../../accounts/accountsTypes';
import { featureFlagsSelectors } from '../../feature-flags';
import { institutionLoginsSelectors, institutionLoginsUtils } from '../../institution-logins';
import type { AggStatus, InstitutionLogin } from '../../institution-logins/institutionLoginsTypes';
import { doAccountRediscovery } from '../accountDiscoveryActions';
import type { AggAction } from '../accountDiscoveryTypes';
import { isForcedFiMigration } from '../fiMigrationUtils';
import { getQcsAggregationAction } from './errors';

const log = getLogger('aggErrors/selectors');

type AggErrorsState = Parameters<typeof institutionLoginsSelectors.getInstitutionLoginForId>[0] &
  Parameters<typeof accountsSelectors.getAccountsByInstitutionLoginId>[0];

const DEAD_ACCOUNT_ERROR = 'DEAD_ACCOUNT';
const DEAD_ACCOUNT_AGG_STATUSES: ReadonlySet<string> = new Set<AggStatus>([
  'ACCOUNT_MISMATCH',
  'ACCOUNT_NOT_FOUND',
  'CP_ACCOUNT_NOT_FOUND',
]);

const isDeadAccountAggStatus = (aggStatus: string | null | undefined): boolean =>
  Boolean(aggStatus && DEAD_ACCOUNT_AGG_STATUSES.has(aggStatus));

export const isAggActionForAccount = (
  aggAction: AggAction | null | undefined,
  account: Account | null | undefined,
): boolean =>
  aggAction?.error !== DEAD_ACCOUNT_ERROR ||
  !account ||
  isDeadAccountAggStatus(account.aggregators?.first()?.aggStatus);

export const getAggregationActionForLogin = (
  institutionLogin: InstitutionLogin | null | undefined,
  accountsForLoginId: List<Account> | undefined,
  featureFlags: Parameters<typeof getQcsAggregationAction>[3],
): AggAction | null | undefined => {
  // todo: currently this selector only handles a single aggregator, need to update to support multiple aggregators
  if (
    !institutionLogin ||
    !institutionLogin.aggregators ||
    institutionLogin.aggregators.size === 0 ||
    !institutionLogin.isConnected
  ) {
    return null;
  }
  if (institutionLogin.aggregators.size > 1) {
    log.warn('Multiple aggregators are not supported');
  }
  const aggregator = institutionLogin.aggregators.get(0);
  if (
    isForcedFiMigration(institutionLogin) ||
    (aggregator!.aggStatus && aggregator!.aggStatus !== 'OK') ||
    institutionLogin.optionalAction
  ) {
    return getQcsAggregationAction(institutionLogin, aggregator!.aggStatus, aggregator!.aggStatusCode, featureFlags);
  }

  if (!accountsForLoginId || accountsForLoginId.size === 0) {
    // todo: return no accounts error...
    return null;
  }

  // todo: handle DEAD accounts error

  let numDeadAccounts = 0;
  let highestStatusToReport = null;
  let highestStatusCodeToReport = null;
  for (let i = 0; i < accountsForLoginId.size; ++i) {
    const account = accountsForLoginId.get(i);
    if (!account?.aggregators || typeof account.aggregators !== 'object') {
      continue;
    }
    // todo: currently this selector only handles a single aggregator, need to update to support multiple aggregators
    if (account.aggregators.size > 1) {
      log.warn('Multiple aggregators are not supported');
    }
    const accountAggregator = account.aggregators.get(0)!;
    if (isDeadAccountAggStatus(accountAggregator.aggStatus)) {
      if (!highestStatusToReport) {
        highestStatusToReport = accountAggregator.aggStatus;
        highestStatusCodeToReport = accountAggregator.aggStatusCode;
      }
      numDeadAccounts += 1;
    }
  }
  if (numDeadAccounts > 0) {
    let accountText = null;
    if (numDeadAccounts === 1) {
      if (accountsForLoginId.size === 1) {
        // We have lost connectivy to your account
        accountText = 'your account';
      } else {
        // We have lost connectivity to one of your accounts
        accountText = 'one of your accounts';
      }
    } else if (numDeadAccounts === accountsForLoginId.size) {
      // We have lost connectivy to all of your accounts
      accountText = 'all of your accounts';
    } else {
      // We have lost connectivity to X of your accounts
      accountText = `${numDeadAccounts} of your accounts`;
    }
    const aggAction: AggAction = {
      // 'ccscrape.324',
      error: DEAD_ACCOUNT_ERROR,
      title: 'This financial institution needs your attention',
      text: `We have lost connectivity to ${accountText}. To restore connectivity, you must reconnect with your FI.`,
      status: highestStatusToReport,
      statusCode: highestStatusCodeToReport,
      code: 'lost.connectivity',
      actionLabel: 'Reconnect',
      action: doAccountRediscovery(institutionLogin),
      showConfirmationDialog: true,
      institutionLogin,
    };
    return aggAction;
  }

  return null;
};

export const getAggregationAction = createSelector(
  (state: unknown, props: { institutionLoginId: string }) =>
    institutionLoginsSelectors.getInstitutionLoginForId(state as AggErrorsState, props.institutionLoginId),
  (state: unknown) => accountsSelectors.getAccountsByInstitutionLoginId(state as AggErrorsState),
  (institutionLogin, accountsByLoginId): AggAction | null | undefined =>
    getAggregationActionForLogin(
      institutionLogin,
      institutionLogin ? accountsByLoginId.get(institutionLogin.id) : undefined,
      featureFlagsSelectors.dangerouslyUseFeatureFlags(),
    ),
);

// Note: A2A banner visibility is intentionally excluded.
export const getStatusBannerVisibilityForInstitutionLogin = createSelector(
  (state: unknown, institutionLoginId: string | null | undefined) =>
    institutionLoginsSelectors.getInstitutionLoginForId(state as AggErrorsState, institutionLoginId || ''),
  (state: unknown, institutionLoginId: string | null | undefined, showDismissed?: boolean) =>
    institutionLoginsSelectors.getFiIssueForLogin(state as AggErrorsState, institutionLoginId || '', showDismissed),
  (state: unknown, institutionLoginId: string | null | undefined) =>
    getAggregationAction(state, { institutionLoginId: institutionLoginId || '' }),
  (institutionLogin, fiIssue, aggAction) => {
    const isAggError =
      institutionLogin &&
      aggAction &&
      institutionLoginsUtils.isErrorType(aggAction?.status as AggStatus | null | undefined);

    const isAppleWallet = institutionLogin?.channel === 'APPLE_WALLET';
    const lastRefreshSuccessfulAt = institutionLogin?.aggregators?.first?.()?.lastRefreshSuccessfulAt;
    const lastSyncAt = lastRefreshSuccessfulAt ? DateTime.fromISO(lastRefreshSuccessfulAt) : null;
    const appleWalletShowWarning = !isAggError && lastSyncAt ? lastSyncAt.diffNow('days').days < -1 : false;
    const appleWalletVisible = Boolean(isAppleWallet && (isAggError || appleWalletShowWarning));

    const fiIssueInfoVisible = Boolean(fiIssue && fiIssue.issueType === 'INFO');
    const institutionVisible = Boolean(isAggError || fiIssueInfoVisible);
    const isForcedMigration = isForcedFiMigration(institutionLogin);

    const canShowCreditInfo = !appleWalletVisible && !institutionVisible;
    const activeBanner = appleWalletVisible ? 'apple-wallet' : institutionVisible ? 'institution' : null;

    return {
      institutionLogin,
      aggAction,
      isAggError,
      isForcedMigration,
      appleWalletVisible,
      appleWalletShowWarning,
      fiIssue,
      fiIssueInfoVisible,
      institutionVisible,
      canShowCreditInfo,
      activeBanner,
    };
  },
);
